import { getCookie } from "../cookie/getCookie";

/**
 * 从浏览器环境中获取userId
 *
 * @description
 * 按以下优先级获取userId:
 * 1. Cookie中的'userId'字段
 * 2. UserAgent中'userid/'后的字符串
 *
 * @returns {string} userId
 * - 成功获取返回对应的userId字符串
 * - 获取失败返回空字符串 ''
 *
 * @throws {never} 该函数会捕获所有错误并返回空字符串
 *
 * @example
 * ```ts
 * // Cookie中存在userId
 * document.cookie = 'userId=12345';
 * getUserId(); // => "12345"
 *
 * // UA中存在userId
 * // UA: Mozilla/5.0 ... userid/67890 ...
 * getUserId(); // => "67890"
 *
 * // 都不存在时
 * getUserId(); // => ""
 *
 * // SSR环境下
 * getUserId(); // => ""
 * ```
 */

/**
 * 从UserAgent中获取userId
 *
 * @param ua - UserAgent字符串
 * @returns 获取到的userId，失败返回空字符串
 */
export function getUserIdFromUA(): string {
  try {
    const ua = navigator?.userAgent || "";
    const USER_ID_PREFIX = "userid/";
    const USER_ID_PREFIX_LENGTH = USER_ID_PREFIX.length;
    // 不区分大小写查找
    const userIndex = ua.toLowerCase().indexOf(USER_ID_PREFIX);
    if (userIndex > -1) {
      const afterUserId = ua.slice(userIndex + USER_ID_PREFIX_LENGTH);
      const USER_ID_PATTERN = /^([^;\s]+)/;
      const match = USER_ID_PATTERN.exec(afterUserId);
      return match?.[1] ?? "";
    }
    return "";
  } catch (error) {
    console.warn("从UA获取userId失败:", error);
    return "";
  }
}

export function getUserId(): string {
  try {
    // 检查SSR环境
    if (
      typeof window === "undefined" ||
      typeof navigator === "undefined" ||
      typeof document === "undefined"
    ) {
      return "";
    }

    // 优先从cookie中获取
    const cookieUserId = getCookie("userId");
    if (cookieUserId) {
      return cookieUserId;
    }

    // 从UA中获取
    return getUserIdFromUA();
  } catch (error) {
    console.warn("获取userId失败:", error);
    return "";
  }
}
